'use client';

/**
 * usePixelTracking Hook & Provider
 *
 * SDK-level pixel tracking based on runtime stepConfig.pixels injected
 * by the CRM. Uses core/pixelMapping for event mapping and gating,
 * keeping browser-specific init/fire logic here.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { GTMTrackingConfig } from '../../core/funnelClient';
import {
  resolvePixelEvents,
  type StandardPixelEvent,
  type PixelProviderKey,
} from '../../core/pixelMapping';
import { useStepConfig } from './useStepConfig';

export type { StandardPixelEvent } from '../../core/pixelMapping';

export interface PixelTrackingContextValue {
  track: (eventName: StandardPixelEvent, parameters?: Record<string, unknown>) => void;
  pixelsInitialized: boolean;
}

const PixelTrackingContext = createContext<PixelTrackingContextValue | null>(null);

// ---------------------------------------------------------------------------
// Duplicate guard (time-window based)
// ---------------------------------------------------------------------------

function createDuplicateGuard(windowMs: number) {
  const lastEvents = new Map<string, number>();
  return (eventName: StandardPixelEvent, parameters: Record<string, unknown>): boolean => {
    try {
      const key = JSON.stringify({ eventName, parameters });
      const now = Date.now();
      const last = lastEvents.get(key);
      if (last && now - last < windowMs) return false;
      lastEvents.set(key, now);
      return true;
    } catch {
      return true;
    }
  };
}

const shouldTrackEvent = createDuplicateGuard(5000);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function PixelTrackingProvider({ children }: { children: React.ReactNode }) {
  const { pixels } = useStepConfig();
  const [pixelsInitialized, setPixelsInitialized] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // ---- Initialize pixel scripts once ----
  // Wait for all external scripts to actually load before marking initialized,
  // so that pixel helpers (TikTok, etc.) can intercept events properly.
  useEffect(() => {
    if (!pixels || pixelsInitialized || !isMountedRef.current) return;

    const loadPromises: Promise<void>[] = [];

    try {
      pixels.facebook?.forEach((px) => { if (px.enabled && px.pixelId) loadPromises.push(initMetaPixel(px.pixelId)); });
      pixels.tiktok?.forEach((px) => { if (px.enabled && px.pixelId) loadPromises.push(initTikTokPixel(px.pixelId)); });
      pixels.snapchat?.forEach((px) => { if (px.enabled && px.pixelId) loadPromises.push(initSnapchatPixel(px.pixelId)); });
      pixels.pinterest?.forEach((px) => { if (px.enabled && px.pixelId) loadPromises.push(initPinterestPixel(px.pixelId)); });
      pixels.gtm?.forEach((px) => { if (px.enabled && (px as GTMTrackingConfig).containerId) loadPromises.push(initGTM((px as GTMTrackingConfig).containerId)); });

      Promise.all(loadPromises).then(() => {
        if (isMountedRef.current) setPixelsInitialized(true);
      });
    } catch (error) {
      console.error('[SDK Pixels] Initialization error:', error);
    }
  }, [pixels, pixelsInitialized]);

  // ---- Track function ----
  const track = useCallback(
    (eventName: StandardPixelEvent, parameters: Record<string, unknown> = {}) => {
      if (!pixels || !pixelsInitialized || !isMountedRef.current) return;
      if (!shouldTrackEvent(eventName, parameters)) return;

      try {
        const events = resolvePixelEvents(pixels, eventName, parameters);
        // Deduplicate by provider: Meta/Snapchat/Pinterest SDKs broadcast
        // to all registered pixel IDs internally, so we only need to fire once per
        // provider. GTM and TikTok need per-pixel firing (GTM for Google Ads
        // send_to targeting, TikTok because ttq.instance() targets a specific pixel).
        const firedProviders = new Set<string>();
        for (const { provider, mapped, pixel } of events) {
          if (provider === 'gtm' || provider === 'tiktok') {
            fire(provider, mapped.name, mapped.params, pixel);
          } else {
            if (!firedProviders.has(provider)) {
              fire(provider, mapped.name, mapped.params);
              firedProviders.add(provider);
            }
          }
        }
      } catch (error) {
        console.error('[SDK Pixels] Tracking error:', error);
      }
    },
    [pixels, pixelsInitialized],
  );

  // ---- Auto page-view ----
  useEffect(() => {
    if (!pixelsInitialized || !isMountedRef.current) return;
    const id = setTimeout(() => {
      if (isMountedRef.current) {
        track('PageView', { path: typeof window !== 'undefined' ? window.location.pathname : '' });
      }
    }, 0);
    return () => clearTimeout(id);
  }, [pixelsInitialized, track]);

  const value = useMemo<PixelTrackingContextValue>(
    () => ({ track, pixelsInitialized }),
    [track, pixelsInitialized],
  );

  return <PixelTrackingContext.Provider value={value}>{children}</PixelTrackingContext.Provider>;
}

export function usePixelTracking(): PixelTrackingContextValue {
  const context = useContext(PixelTrackingContext);
  if (!context) throw new Error('usePixelTracking must be used within a PixelTrackingProvider');
  return context;
}

// ---------------------------------------------------------------------------
// Browser-specific: fire an event to the correct global pixel function
// ---------------------------------------------------------------------------

/* eslint-disable @typescript-eslint/no-explicit-any */
function fire(provider: PixelProviderKey, name: string, params: Record<string, unknown>, pixel?: import('../../core/funnelClient').PixelConfig) {
  if (typeof window === 'undefined') return;
  const w = window as any;
  switch (provider) {
    case 'facebook':  w.fbq?.('track', name, params); break;
    case 'tiktok': {
      // Use ttq.instance(pixelId) to target each pixel individually,
      // since the global ttq.track() only fires for the sdkid pixel.
      const pixelId = pixel && 'pixelId' in pixel ? (pixel as any).pixelId : null;
      const target = pixelId && w.ttq?.instance ? w.ttq.instance(pixelId) : w.ttq;
      if (name === 'Pageview') { target?.page?.(); }
      else { target?.track?.(name, params); }
      break;
    }
    case 'snapchat':  w.snaptr?.('track', name, params); break;
    case 'pinterest':
      // Pinterest handles page views via pintrk('page'), not pintrk('track', 'pagevisit')
      if (name === 'pagevisit') { w.pintrk?.('page'); }
      else { w.pintrk?.('track', name, params); }
      break;
    case 'gtm':       fireGTM(name, params); break;
  }
}

function fireGTM(event: string, params: Record<string, unknown>) {
  try {
    const w = window as any;
    if (w.gtag) {
      w.gtag('event', event, params);
    } else if (w.dataLayer) {
      w.dataLayer.push({ event, ...params });
    }
  } catch (error) {
    console.error('[SDK GTM] Error:', error);
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

// ===========================================================================
// Pixel initialization (browser-only)
// All pixel globals are accessed via `win` typed as `any` to avoid
// `declare global` augmentation issues across tsconfig scopes.
// ===========================================================================

/* eslint-disable @typescript-eslint/no-explicit-any */
const win = (typeof window !== 'undefined' ? window : undefined) as any;

const SCRIPT_LOAD_TIMEOUT_MS = 5000;

function waitForScriptLoad(script: HTMLScriptElement): Promise<void> {
  // If the script has already loaded, resolve immediately
  if (script.dataset.loaded) return Promise.resolve();

  return new Promise((resolve) => {
    const done = () => { script.dataset.loaded = '1'; clearTimeout(timer); resolve(); };
    const timer = setTimeout(done, SCRIPT_LOAD_TIMEOUT_MS);
    script.addEventListener('load', done, { once: true });
    script.addEventListener('error', done, { once: true });
  });
}

let _metaScriptEl: HTMLScriptElement | null = null;

function initMetaPixel(pixelId: string): Promise<void> {
  if (!win) return Promise.resolve();

  // Initialize Meta base code once
  if (!win.fbq) {
    const n = function (...args: any[]) {
      if (n.callMethod) n.callMethod(...args); else n.queue.push(args);
    } as any;
    n.queue = [];
    n.loaded = true;
    n.version = '2.0';
    win.fbq = n;
    if (!win._fbq) win._fbq = n;
    const t = document.createElement('script') as HTMLScriptElement;
    t.async = true;
    t.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const s = document.getElementsByTagName('script')[0];
    s?.parentNode?.insertBefore(t, s);
    _metaScriptEl = t;
  }

  // Register each pixel ID (fbq supports multiple pixels via multiple init calls)
  win.fbq('init', pixelId);
  return _metaScriptEl ? waitForScriptLoad(_metaScriptEl) : Promise.resolve();
}

let _tiktokBaseInitialized = false;

function initTikTokPixel(pixelId: string): Promise<void> {
  if (!win) return Promise.resolve();

  // Initialize TikTok base code once
  if (!_tiktokBaseInitialized) {
    _tiktokBaseInitialized = true;
    win.TiktokAnalyticsObject = 'ttq';
    const ttq = (win.ttq = win.ttq || []) as any;
    ttq.methods = [
      'page', 'track', 'identify', 'instances', 'debug', 'on', 'off',
      'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie',
      'holdConsent', 'revokeConsent', 'grantConsent',
    ];
    ttq.setAndDefer = function (t: any, e: string) {
      t[e] = function (...args: any[]) { t.push([e, ...args]); };
    };
    for (const method of ttq.methods) {
      ttq.setAndDefer(ttq, method);
    }
    ttq.instance = function (t: string) {
      const e = ttq._i[t] || [];
      for (const method of ttq.methods) {
        ttq.setAndDefer(e, method);
      }
      return e;
    };
    ttq.load = function (e: string, n?: any) {
      const r = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      ttq._i = ttq._i || {};
      ttq._i[e] = [];
      ttq._i[e]._u = r;
      ttq._t = ttq._t || {};
      ttq._t[e] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[e] = n || {};
      const s = document.createElement('script') as HTMLScriptElement;
      s.type = 'text/javascript';
      s.async = true;
      s.src = r + '?sdkid=' + e + '&lib=ttq';
      const p = document.getElementsByTagName('script')[0];
      p?.parentNode?.insertBefore(s, p);
    };
  }

  // Skip if this specific pixel ID is already registered
  if (win.ttq._i?.[pixelId]) return Promise.resolve();

  // Register pixel and load its script
  win.ttq.load(pixelId);

  // Find the script we just created and wait for it
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    'script[src*="analytics.tiktok.com/i18n/pixel/events.js"]'
  );
  const lastScript = scripts[scripts.length - 1];
  return lastScript ? waitForScriptLoad(lastScript) : Promise.resolve();
}

let _snapchatScriptEl: HTMLScriptElement | null = null;

function initSnapchatPixel(pixelId: string): Promise<void> {
  if (!win) return Promise.resolve();

  // Initialize Snapchat base code once
  if (!win.snaptr) {
    const a = function (...args: any[]) {
      if (a.handleRequest) a.handleRequest(...args); else a.queue.push(args);
    } as any;
    a.queue = [];
    win.snaptr = a;
    const r = document.createElement('script') as HTMLScriptElement;
    r.async = true;
    r.src = 'https://sc-static.net/scevent.min.js';
    const u = document.getElementsByTagName('script')[0];
    u?.parentNode?.insertBefore(r, u);
    _snapchatScriptEl = r;
  }

  // Register each pixel ID (snaptr supports multiple pixels via multiple init calls)
  win.snaptr('init', pixelId);
  return _snapchatScriptEl ? waitForScriptLoad(_snapchatScriptEl) : Promise.resolve();
}

let _pinterestScriptEl: HTMLScriptElement | null = null;

function initPinterestPixel(pixelId: string): Promise<void> {
  if (!win) return Promise.resolve();

  // Initialize Pinterest base code once
  if (!win.pintrk) {
    const a = function (...args: any[]) { a.queue.push(args); } as any;
    a.queue = [];
    win.pintrk = a;
    const s = document.createElement('script') as HTMLScriptElement;
    s.async = true;
    s.src = 'https://s.pinimg.com/ct/core.js';
    const u = document.getElementsByTagName('script')[0];
    u?.parentNode?.insertBefore(s, u);
    _pinterestScriptEl = s;
  }

  // Register each pixel ID (pintrk supports multiple pixels via multiple load calls)
  win.pintrk('load', pixelId);
  // Note: pintrk('page') is NOT called here — the Provider's auto page-view
  // effect handles it via fire(). Calling both would double-count page views.
  return _pinterestScriptEl ? waitForScriptLoad(_pinterestScriptEl) : Promise.resolve();
}

function initGTM(containerId: string): Promise<void> {
  if (!win || !containerId) return Promise.resolve();
  const isGtmContainer = containerId.startsWith('GTM-');
  const scriptUrlPart = isGtmContainer
    ? `googletagmanager.com/gtm.js?id=${containerId}`
    : `googletagmanager.com/gtag/js?id=${containerId}`;
  if (document.querySelector(`script[src*="${scriptUrlPart}"]`)) return Promise.resolve();

  win.dataLayer = win.dataLayer || [];

  // Push referrer domain context into dataLayer before GTM loads, so tags
  // inside the container can use it for cross-domain tracking configuration.
  try {
    const ref = document.referrer && new URL(document.referrer).hostname;
    if (ref && ref !== window.location.hostname) {
      win.dataLayer.push({ tagada_referrer_domain: ref });
    }
  } catch { /* ignore invalid referrer */ }

  if (isGtmContainer) {
    win.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    const f = document.getElementsByTagName('script')[0];
    const j = document.createElement('script') as HTMLScriptElement;
    j.async = true;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + containerId;
    if (f?.parentNode) f.parentNode.insertBefore(j, f);
    else (document.head || document.getElementsByTagName('head')[0])?.appendChild(j);
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
    iframe.height = '0'; iframe.width = '0';
    iframe.style.display = 'none'; iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    (document.body || document.getElementsByTagName('body')[0])?.insertBefore(noscript, document.body?.firstChild ?? null);
    return waitForScriptLoad(j);
  } else {
    if (!win.gtag) {
      win.gtag = function (..._args: any[]) { win.dataLayer.push(arguments); };
    }
    win.gtag('js', new Date());

    // Enable cross-domain tracking: accept incoming _gl parameter from referring
    // domains (e.g. Shopify store → TagadaPay checkout) so Google preserves the
    // client-id/session across the redirect.
    const linkerConfig: Record<string, unknown> = { accept_incoming: true };
    try {
      const ref = document.referrer && new URL(document.referrer).hostname;
      if (ref && ref !== window.location.hostname) {
        linkerConfig.domains = [ref, window.location.hostname];
      }
    } catch { /* ignore invalid referrer */ }

    win.gtag('config', containerId, { linker: linkerConfig });
    const script = document.createElement('script') as HTMLScriptElement;
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=' + containerId;
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) firstScript.parentNode.insertBefore(script, firstScript);
    else (document.head || document.getElementsByTagName('head')[0])?.appendChild(script);
    return waitForScriptLoad(script);
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */
